import ListPageController, { type StatCardDef } from "./mixins/ListPageController";
import Fragment from "sap/ui/core/Fragment";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import JSONModel from "sap/ui/model/json/JSONModel";
import type { Select$ChangeEvent } from "sap/m/Select";
import type Dialog from "sap/m/Dialog";
import type Input from "sap/m/Input";
import type Select from "sap/m/Select";
import type ComboBox from "sap/m/ComboBox";
import type DatePicker from "sap/m/DatePicker";
import type TextArea from "sap/m/TextArea";
import type Context from "sap/ui/model/odata/v4/Context";
import type { Button$PressEvent } from "sap/m/Button";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

interface LicenseRow {
	Id: string;
	EntitlementId: string;
	DeploymentId: string;
	Status: string;
}

interface CreateLicenseRequest {
	entitlementId: string;
	deploymentId: string;
	quantity: number;
	licenseType: string | null;
	validFrom: string | null;
	validUntil: string | null;
}

type StatusKey = "ALL" | "Active" | "Suspended" | "Voided" | "Expired";
type TypeKey = "ALL" | "Production" | "Demo" | "Test";

interface ViewState {
	statusFilter: StatusKey;
	typeFilter: TypeKey;
}

interface VariantPreset {
	status: StatusKey;
	type: TypeKey;
}

const VARIANTS: Record<string, VariantPreset> = {
	"standard":  { status: "ALL",       type: "ALL" },
	"active":    { status: "Active",    type: "ALL" },
	"suspended": { status: "Suspended", type: "ALL" },
	"voided":    { status: "Voided",    type: "ALL" },
	"expired":   { status: "Expired",   type: "ALL" }
};

// Karty statystyk nad tabelą — ten sam fragment StatCardsRow co Organizations/Dashboard.
const STATS: readonly StatCardDef[] = [
	{ key: "total", labelKey: "licenses.stats.total.label", subtitleKey: "licenses.stats.total.subtitle", state: "Neutral" },
	{ key: "active", labelKey: "licenses.stats.active.label", subtitleKey: "licenses.stats.active.subtitle", state: "Good", filter: "Status eq 'Active'" },
	{ key: "suspended", labelKey: "licenses.stats.suspended.label", subtitleKey: "licenses.stats.suspended.subtitle", state: "Critical", filter: "Status eq 'Suspended'" },
	{ key: "expired", labelKey: "licenses.stats.expired.label", subtitleKey: "licenses.stats.expired.subtitle", state: "Error", filter: "Status eq 'Expired'" }
];

/**
 * @namespace computec.b2b.portal.controller
 */
export default class Licenses extends ListPageController {
	private bindDialog?: Dialog;
	private voidDialog?: Dialog;
	private voidTargetId?: string;

	public override onInit(): void {
		const initial: ViewState = { statusFilter: "ALL", typeFilter: "ALL" };
		this.getView().setModel(new JSONModel(initial), "view");
		void this.initStatsCards("Licenses", STATS);
		this.getRouter().getRoute("licenses")?.attachPatternMatched(() => { void this.onShow(); });
	}

	private async onShow(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const title = bundle.getText("licenses.title") ?? "Licenses";
		this.setPageHeader({ title, subtitle: bundle.getText("licenses.subtitle") ?? "" });
		this.applyFilters();
		void this.refreshStatsCards();
	}

	public onStatusSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as StatusKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/statusFilter", key);
		this.applyFilters();
	}

	public onTypeSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as TypeKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/typeFilter", key);
		this.applyFilters();
	}

	public onVariantSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? "standard";
		const preset = VARIANTS[key];
		if (!preset) {
			return;
		}
		const model = this.getView().getModel("view") as JSONModel;
		model.setProperty("/statusFilter", preset.status);
		model.setProperty("/typeFilter", preset.type);
		this.applyFilters();
	}

	public onRefresh(): void {
		this.refreshTable("licenseTable");
		void this.refreshStatsCards();
	}

	private applyFilters(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as ViewState;
		const parts: string[] = [];

		if (state.statusFilter !== "ALL") {
			parts.push(`Status eq '${state.statusFilter}'`);
		}
		if (state.typeFilter !== "ALL") {
			parts.push(`LicenseType eq '${state.typeFilter}'`);
		}

		const binding = this.getTableBinding("licenseTable");
		binding?.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}

	// === Bind license ===

	public async onBindLicensePress(): Promise<void> {
		if (!this.bindDialog) {
			this.bindDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.BindLicenseDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.bindDialog);
		}
		(this.byId("cbBindEntitlement") as ComboBox).setSelectedKey("");
		(this.byId("cbBindDeployment") as ComboBox).setSelectedKey("");
		(this.byId("inpBindQuantity") as Input).setValue("1");
		(this.byId("ddBindLicenseType") as Select).setSelectedKey("Production");
		(this.byId("dpBindValidFrom") as DatePicker).setValue("");
		(this.byId("dpBindValidUntil") as DatePicker).setValue("");
		this.bindDialog.open();
	}

	public onCancelBindLicense(): void {
		this.bindDialog?.close();
	}

	public async onConfirmBindLicense(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const entitlementId = (this.byId("cbBindEntitlement") as ComboBox).getSelectedKey();
		const deploymentId = (this.byId("cbBindDeployment") as ComboBox).getSelectedKey();
		const qty = Number((this.byId("inpBindQuantity") as Input).getValue());
		const licenseType = (this.byId("ddBindLicenseType") as Select).getSelectedKey();
		const validFrom = (this.byId("dpBindValidFrom") as DatePicker).getValue();
		const validUntil = (this.byId("dpBindValidUntil") as DatePicker).getValue();

		if (!entitlementId || !deploymentId || !(qty >= 1)) {
			MessageBox.warning(bundle.getText("orders.wizard.invalid") ?? "Fill required fields");
			return;
		}

		const payload: CreateLicenseRequest = {
			entitlementId,
			deploymentId,
			quantity: qty,
			licenseType: licenseType || null,
			validFrom: validFrom || null,
			validUntil: validUntil || null
		};

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json("/api/v1/licenses", {
				method: "POST",
				body: JSON.stringify(payload)
			});
			MessageToast.show(bundle.getText("licenses.bind.success") ?? "License bound");
			this.bindDialog?.close();
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("licenses.bind.failure") ?? "Failed"));
		}
	}

	// === Void license ===

	public async onVoidLicensePress(event: Button$PressEvent): Promise<void> {
		const ctx = event.getSource().getBindingContext("odata") as Context | null;
		if (!ctx) {
			return;
		}
		const row = ctx.getObject() as LicenseRow;
		this.voidTargetId = row.Id;

		if (!this.voidDialog) {
			this.voidDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.VoidLicenseDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.voidDialog);
		}
		(this.byId("taVoidReason") as TextArea).setValue("");
		this.voidDialog.open();
	}

	public onCancelVoidLicense(): void {
		this.voidDialog?.close();
		this.voidTargetId = undefined;
	}

	public async onConfirmVoidLicense(): Promise<void> {
		if (!this.voidTargetId) {
			return;
		}
		const reason = (this.byId("taVoidReason") as TextArea).getValue().trim();
		const bundle = await this.getResourceBundle();
		if (reason.length === 0) {
			MessageBox.warning(bundle.getText("orders.wizard.invalid") ?? "Reason required");
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/licenses/${this.voidTargetId}/void`, {
				method: "POST",
				body: JSON.stringify({ reason })
			});
			MessageToast.show(bundle.getText("licenses.void.success") ?? "License voided");
			this.voidDialog?.close();
			this.voidTargetId = undefined;
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("licenses.void.failure") ?? "Failed"));
		}
	}

}
